<!--
   Tag 详情页（参考 cherry-web/pages/tag.vue）
-->
<template>
  <div class="page">
    <van-sticky>
      <van-nav-bar :title="pageTitle" @click-left="onClickLeft">
        <template #left>
          <van-image
            fit="cover"
            class="nav-back"
            src="https://cdn.qiandaoapp.com/interior/images/07e5d5fa0e6ae47f3be4d9fe31a5c616.png"
            alt=""
          />
        </template>
        <template #right>
          <van-icon name="ellipsis" size="24" />
        </template>
      </van-nav-bar>
    </van-sticky>

    <!-- 下载 App Banner（复用组件） -->
    <download-banner @onInvokeApp="onInvokeApp" />

    <div v-if="loaded" class="tag-content">
      <!-- Tag 卡片 -->
      <div class="tag-card" v-if="header.name">
        <div class="tag-card__cover">
          <van-image fit="cover" class="tag-card__cover-img" :src="coverUrl" />
        </div>
        <div class="tag-card__info">
          <div class="tag-card__name">{{ header.name }}</div>
          <div class="tag-card__sub">{{ followCountText }}</div>
        </div>
        <div class="tag-card__follow" @click="toggleFollow">关注</div>
      </div>

      <!-- 开岛进度卡片 -->
      <div v-if="showIslandProgress" class="island-card">
        <div
          class="island-card__bg"
          :style="{ backgroundImage: coverUrl ? `url(${coverUrl})` : '' }"
        ></div>
        <div class="island-card__inner">
          <div class="island-card__row">
            <div class="island-card__title">
              <span class="island-card__title-text">正在开启岛</span>
              <van-icon name="question-o" class="island-card__help" size="12" />
            </div>
            <div class="island-card__countdown" v-if="countdown">
              <span>活动倒计时</span>
              <span class="island-card__days">{{
                String(countdown.value).padStart(2, "0")
              }}</span>
              <span>{{ countdown.unit }}</span>
            </div>
          </div>

          <div class="island-card__progress">
            <div class="island-card__progress-track"></div>
            <div
              class="island-card__progress-bar"
              :style="{ width: progressPercent + '%' }"
            ></div>
            <div
              class="island-card__progress-pill"
              :style="{ left: labelLeftPercent + '%' }"
            >
              还差{{ remainCount }}人
            </div>
          </div>

          <div class="island-card__bottom">
            <div class="island-card__avatars">
              <template v-for="(slot, idx) in displayAvatars" :key="idx">
                <div
                  v-if="slot.avatar"
                  class="island-card__avatar"
                  :style="{ zIndex: idx + 1 }"
                >
                  <van-image
                    fit="cover"
                    class="island-card__avatar-img"
                    :src="slot.avatar"
                  />
                </div>
                <div
                  v-else
                  class="island-card__avatar island-card__avatar--placeholder"
                  :style="{ zIndex: idx + 1 }"
                >
                  <span class="island-card__avatar-plus">+</span>
                </div>
              </template>
              <span class="island-card__avatar-dots">···</span>
              <div class="island-card__avatar-more">
                <span class="island-card__avatar-plus">+</span>
              </div>
            </div>
            <div class="island-card__invite" @click="onInvite">
              <span>邀请好友</span>
              <DuIcon
                name="share"
                size='3.2rem'
                class="island-card__invite-icon"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 开岛成功：展示已关联的岛 -->
      <div v-else-if="showIsland" class="island-success" @click="onOpenIsland">
        <van-image
          fit="cover"
          class="island-success__image"
          :src="tagIslandInfo.image"
        />
        <span class="island-success__name">{{ tagIslandInfo.name }}</span>
        <span class="island-success__badge">岛</span>
        <van-icon name="arrow" size="10" class="island-success__arrow" />
      </div>

      <!-- 未开启岛：小入口 tag -->
      <div
        v-else-if="showIslandEntry"
        class="island-entry"
        @click="onOpenIsland"
      >
        <div class="island-entry__tag">岛</div>
        <span class="island-entry__text">开启岛</span>
        <van-icon name="arrow" size="10" class="island-entry__arrow" />
      </div>
    </div>

    <div
      class="tag-empty"
      v-if="
        loaded &&
        !header.name &&
        !showIslandProgress &&
        !showIsland &&
        !showIslandEntry
      "
    >
      <div class="tag-empty-txt">找不到该标签</div>
    </div>

    <div ref="appOpenSlotRef" class="tag-app-open-slot">
      <div
        class="tag-app-open"
        :class="{ 'tag-app-open--fixed': isAppOpenFixed }"
      >
        <MainButton
          size="large"
          text="临界App内打开"
          @onInvokeApp="onInvokeApp"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import {
  Image as VanImage,
  NavBar as VanNavBar,
  Sticky as VanSticky,
  Icon as VanIcon,
} from "vant";
import { DuIcon } from "dangoui";
import DownloadBanner from "@/components/DownloadBanner";
import MainButton from "@/components/MainButton";
import NftApi from "@/apis";
import { getQuery } from "@/utils/url";
import { invokeAppByYYB } from "@/utils/mob";

const router = useRouter();
const loaded = ref(false);
const simpleInfo = ref({});
const appOpenSlotRef = ref(null);
const isAppOpenFixed = ref(true);
let appOpenObserver;

const pageTitle = "临界 app - 次元无边界";

// 从 simpleInfo.header 取基础信息（参考 cherry-web/pages/tag.vue）
const header = computed(() => simpleInfo.value?.header || {});
const coverUrl = computed(() => header.value?.cover?.image || "");
// 关注人数文案（接口 relatedText 已格式化好，如 "5人关注" / "1.2万人关注"）
const followCountText = computed(() => header.value?.relatedText || "");

// 开岛活动（simpleInfo.generateIsland）
const generateIsland = computed(() => simpleInfo.value?.generateIsland || {});
const activity = computed(
  () => generateIsland.value?.createIslandActivity || {}
);
const showIslandProgress = computed(() => !!activity.value?.boostFesId);

const tagIslandInfo = computed(() => simpleInfo.value?.tagIslandInfo || {});

const showIsland = computed(() => !!tagIslandInfo.value?.id);

const showIslandEntry = computed(
  () => generateIsland.value?.displayCreateIslandActivityFlag === true
);

const progressPercent = computed(() => {
  const total = +activity.value?.totalNum || 0;
  const joined = +activity.value?.successNum || 0;
  if (!total) return 0;
  return Math.min(100, Math.round((joined / total) * 100));
});

const labelLeftPercent = computed(() =>
  Math.max(8, Math.min(92, progressPercent.value))
);

const remainCount = computed(() => {
  const total = +activity.value?.totalNum || 0;
  const joined = +activity.value?.successNum || 0;
  return Math.max(0, total - joined);
});

const AVATAR_DISPLAY_COUNT = 4;
const displayAvatars = computed(() => {
  const list = (activity.value?.participatedUserList || []).slice(
    0,
    AVATAR_DISPLAY_COUNT
  );
  const placeholders = Array.from(
    { length: AVATAR_DISPLAY_COUNT - list.length },
    () => ({})
  );
  return [...list, ...placeholders];
});

// expireAt 兼容秒/毫秒，统一返回毫秒时间戳
const expireMs = computed(() => {
  const expire = +activity.value?.expireAt;
  if (!expire) return 0;
  return expire < 1e12 ? expire * 1000 : expire;
});

const countdown = computed(() => {
  if (!expireMs.value) return null;
  const diff = expireMs.value - Date.now();
  if (diff <= 0) return null;

  const DAY = 24 * 3600 * 1000;
  const HOUR = 3600 * 1000;
  const MINUTE = 60 * 1000;

  if (diff >= DAY) {
    return { value: Math.ceil(diff / DAY), unit: "天" };
  }

  const hours = Math.ceil(diff / HOUR);
  if (hours >= 24) return { value: 1, unit: "天" };
  if (diff >= HOUR) return { value: hours, unit: "小时" };

  const minutes = Math.ceil(diff / MINUTE);
  if (minutes >= 60) return { value: 1, unit: "小时" };
  return { value: Math.max(1, minutes), unit: "分钟" };
});

const onClickLeft = () => {
  router.back();
};

const onInvokeApp = () => {
  const { id } = getQuery();
  invokeAppByYYB({
    targetPath: "pages-market/tag/index",
    params: { tagId: id },
  });
};

const toggleFollow = () => {
  onInvokeApp();
};

const onInvite = () => {
  onInvokeApp();
};

const onOpenIsland = () => {
  onInvokeApp();
};

const getTagDetail = async () => {
  const { id } = getQuery();
  if (!id) {
    loaded.value = true;
    return;
  }
  try {
    const simpleRes = await NftApi.getTagSimpleInfo(id);
    const info = simpleRes?.data || {};
    simpleInfo.value = info;
  } catch (e) {
    console.error("getTagDetail failed", e);
    simpleInfo.value = {};
  } finally {
    loaded.value = true;
  }
};

onMounted(() => {
  getTagDetail();

  if (appOpenSlotRef.value && window.IntersectionObserver) {
    appOpenObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 0) {
          isAppOpenFixed.value = true;
        }
        if (entry.intersectionRatio === 1) {
          isAppOpenFixed.value = false;
        }
      },
      {
        rootMargin: "0px",
        threshold: [0, 1],
      }
    );
    appOpenObserver.observe(appOpenSlotRef.value);
  }
});

onBeforeUnmount(() => {
  if (appOpenObserver && appOpenSlotRef.value) {
    appOpenObserver.unobserve(appOpenSlotRef.value);
    appOpenObserver.disconnect();
  }
});
</script>

<style lang="scss" scoped>
.page {
  display: flex;
  flex-direction: column;
  font-size: 24rpx;
  position: relative;
  min-height: 100vh;
  background-color: #fff;
  box-sizing: border-box;
}

.tag-app-open-slot {
  display: flex;
  justify-content: center;
  width: 100%;
  height: calc(116rpx + env(safe-area-inset-bottom));
  margin-top: auto;
  flex-shrink: 0;
}

.tag-app-open {
  width: 354rpx;
  height: 96rpx;

  &--fixed {
    position: fixed;
    z-index: 1000;
    left: 50%;
    bottom: calc(20rpx + env(safe-area-inset-bottom));
    transform: translateX(-50%);
  }
}

.nav-back {
  width: 66rpx;
  height: 66rpx;
  border-radius: 100%;
  box-shadow: 0 8rpx 40rpx rgba(0, 0, 0, 0.06);
}
.tag-content {
  margin-top: 12rpx;
  padding: 8rpx 10rpx 12rpx 10rpx;
}
/* Tag 卡片（设计稿：Category） */
.tag-card {
  display: flex;
  align-items: center;
  gap: 24rpx;
  background: #fff;
  border-radius: 16rpx 16rpx 0 0;

  &__cover {
    width: 162rpx;
    height: 162rpx;
    border-radius: 16rpx;
    overflow: hidden;
    flex-shrink: 0;
    background: #eee;
  }
  &__cover-img {
    width: 100%;
    height: 100%;
  }
  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8rpx;
  }
  &__name {
    font-family: "PingFang SC", sans-serif;
    font-size: 40rpx;
    font-weight: 500;
    line-height: 52rpx;
    color: #000;
  }
  &__sub {
    font-size: 24rpx;
    line-height: 36rpx;
    color: rgba(0, 0, 0, 0.64);
  }
  &__follow {
    display: flex;
    width: 136rpx;
    padding: 12rpx 32rpx;
    justify-content: center;
    align-items: center;
    gap: 10rpx;
    border-radius: 12rpx;
    background: #0d002d;
    box-sizing: border-box;
    color: #fff;
    font-family: "PingFang SC", sans-serif;
    font-size: 24rpx;
    font-weight: 500;
    line-height: 36rpx;
    flex-shrink: 0;
  }
}

.island-success {
  display: inline-flex;
  align-items: center;
  gap: 8rpx;
  max-width: 100%;
  margin-top: 8rpx;
  padding: 6rpx 8rpx;
  background: #f8f8f8;
  border-radius: 12rpx;

  &__image {
    width: 40rpx;
    height: 40rpx;
    border-radius: 6rpx;
    overflow: hidden;
    flex-shrink: 0;
  }
  &__name {
    overflow: hidden;
    color: rgba(0, 0, 0, 0.64);
    font-size: 28rpx;
    font-weight: 500;
    line-height: 40rpx;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32rpx;
    height: 32rpx;
    padding: 4rpx 6rpx;
    border-radius: 4rpx;
    background: #7247dc;
    box-sizing: border-box;
    color: #fff;
    font-size: 20rpx;
    font-weight: 500;
    flex-shrink: 0;
  }
  &__arrow {
    color: rgba(0, 0, 0, 0.4);
    flex-shrink: 0;
  }
}

/* 未开启岛 - 小入口 tag（设计稿：👻 Entry / Tag） */
.island-entry {
  display: inline-flex;
  align-items: center;
  gap: 8rpx;
  margin-top: 8rpx;
  padding: 5rpx 8rpx;
  height: 36rpx;
  background: #f8f8f8;
  border-radius: 6rpx;

  &__tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16rpx;
    height: 16rpx;
    padding: 3rpx;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 4rpx;
    font-family: "PingFang SC", sans-serif;
    font-size: 10rpx;
    line-height: 22rpx;
    color: rgba(0, 0, 0, 0.64);
  }
  &__text {
    font-family: "PingFang SC", sans-serif;
    font-size: 12rpx;
    font-weight: 500;
    line-height: 36rpx;
    color: rgba(0, 0, 0, 0.64);
  }
  &__arrow {
    color: rgba(0, 0, 0, 0.64);
  }
}

/* 开岛进度卡片（设计稿：Entry / Assisting） */
.island-card {
  margin-top: 8rpx;
  padding: 12rpx;
  position: relative;
  border-radius: 8rpx;
  overflow: hidden;
  background: #fff;

  /* 背景：cover 图 + 渐变到 #f8f8f8 */
  &__bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    background-color: #f8f8f8;
    &::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(
        90deg,
        #f8f8f8 0%,
        rgba(248, 248, 248, 0.9) 50%,
        #f8f8f8 100%
      );
    }
  }

  &__inner {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 10rpx;
  }
  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__title {
    display: flex;
    align-items: center;
    gap: 8rpx;
  }
  &__title-text {
    font-family: "Alibaba PuHuiTi", "PingFang SC", sans-serif;
    font-size: 32rpx;
    font-weight: 800;
    line-height: 48rpx;
    color: #000;
  }
  &__help {
    color: rgba(0, 0, 0, 0.4);
  }
  &__countdown {
    display: flex;
    align-items: center;
    gap: 8rpx;
    font-family: "PingFang SC", sans-serif;
    font-size: 24rpx;
    line-height: 36rpx;
    color: #000;
  }
  &__days {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40rpx;
    height: 40rpx;
    padding: 0 8rpx;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 8rpx;
    font-size: 24rpx;
    color: rgba(0, 0, 0, 0.64);
  }

  /* 进度条 */
  &__progress {
    position: relative;
    height: 30rpx;
  }
  &__progress-track {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    height: 16rpx;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 32rpx;
  }
  &__progress-bar {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    height: 16rpx;
    background: linear-gradient(90deg, rgba(0, 0, 0, 0.24) 0%, #000 100%);
    border-radius: 32rpx;
    transition: width 0.3s ease;
  }
  &__progress-pill {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    padding: 4rpx 8rpx;
    background: #000;
    color: #fff;
    border-radius: 40rpx;
    font-family: "PingFang SC", sans-serif;
    font-size: 20rpx;
    font-weight: 500;
    line-height: 22rpx;
    white-space: nowrap;
    box-shadow: 0 8rpx 40rpx rgba(0, 0, 0, 0.06);
  }

  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__avatars {
    display: flex;
    align-items: center;
  }
  &__avatar {
    width: 48rpx;
    height: 48rpx;
    border-radius: 50%;
    overflow: hidden;
    border: 2rpx solid #fff;
    margin-right: -16rpx;
    background: #eee;
    flex-shrink: 0;

    &--placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fff;
      border: 2rpx dashed rgba(0, 0, 0, 0.12);
      box-sizing: border-box;
    }
  }
  &__avatar-img {
    width: 100%;
    height: 100%;
  }
  &__avatar-plus {
    color: rgba(0, 0, 0, 0.64);
    font-size: 28rpx;
    font-weight: 300;
    line-height: 48rpx;
  }
  &__avatar-more {
    width: 48rpx;
    height: 48rpx;
    border-radius: 50%;
    background: #fff;
    border: 2rpx dashed rgba(0, 0, 0, 0.12);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  &__avatar-dots {
    margin-left: 24rpx;
    margin-right: 8rpx;
    color: rgba(0, 0, 0, 0.4);
    font-size: 24rpx;
    line-height: 36rpx;
  }

  &__invite {
    display: flex;
    align-items: center;
    gap: 8rpx;
    padding: 10rpx 20rpx;
    background: #7247dc;
    color: #fff;
    border-radius: 8rpx;
    font-family: "PingFang SC", sans-serif;
    font-size: 24rpx;
    font-weight: 500;
    line-height: 36rpx;
  }
  &__invite-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24rpx;
    height: 24rpx;
    flex-shrink: 0;
  }
}

.tag-empty {
  margin: 30vh auto 0 auto;
  text-align: center;
  &-txt {
    color: rgba(0, 0, 0, 0.5);
    font-size: 28rpx;
  }
}
</style>
